import React from "react";
import { Modal, Spin } from "antd";
import dayjs from "dayjs";
import "./ConfirmationForClassReservationPopup.scss";
import { useTranslation } from "react-i18next";
import RedeemLoyaltyCheckbox from "../RedeemLoyaltyCheckbox/RedeemLoyaltyCheckbox";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  startISO: string;
  endISO?: string;
  durationMin?: number | null;
  instructor?: string | null;
  credits?: number;
  refundCancellationHours: number;
  isLoyaltyDealAvailable: boolean;
  setIsUsingLoyalty: (value: boolean) => void;
  loading?: boolean;
}

export const ConfirmationForClassReservationPopup: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  title,
  startISO,
  endISO,
  durationMin,
  instructor,
  credits,
  refundCancellationHours,
  isLoyaltyDealAvailable,
  setIsUsingLoyalty,
  loading = false,
}) => {
  const { t } = useTranslation();
  const start = dayjs(startISO);
  const end = endISO ? dayjs(endISO).format("h:mm A") : "";

  const dateLineClasses = `${start.format("ddd, MMM D, YYYY")} – ${start.format("h:mm A")} (${
    durationMin ?? 0
  } min)`;
  const dateLineOpenGyms = `${start.format("ddd, MMM D, YYYY")} ${start.format("h:mm A")} - ${end}`;

  return (
    <Modal
      open={open}
      centered
      footer={null}
      onCancel={onClose}
      destroyOnClose
      className="confirmation-modal"
    >
      <div className="confirmation-modal__content">
        <h2 className="confirmation-modal__title">{title}</h2>

        <div className="confirmation-modal__row">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <g clipPath="url(#clip0_28279_21587)">
              <path
                d="M16 3C16.2449 3.00003 16.4813 3.08996 16.6644 3.25272C16.8474 3.41547 16.9643 3.63975 16.993 3.883L17 4V5H19C19.5046 4.99984 19.9906 5.19041 20.3605 5.5335C20.7305 5.87659 20.9572 6.34684 20.995 6.85L21 7V19C21.0002 19.5046 20.8096 19.9906 20.4665 20.3605C20.1234 20.7305 19.6532 20.9572 19.15 20.995L19 21H5C4.49542 21.0002 4.00943 20.8096 3.63945 20.4665C3.26947 20.1234 3.04284 19.6532 3.005 19.15L3 19V7C2.99984 6.49542 3.19041 6.00943 3.5335 5.63945C3.87659 5.26947 4.34684 5.04284 4.85 5.005L5 5H7V4C7.00028 3.74512 7.09788 3.49997 7.27285 3.31463C7.44782 3.1293 7.68695 3.01777 7.94139 3.00283C8.19584 2.98789 8.44638 3.07067 8.64183 3.23426C8.83729 3.39786 8.9629 3.6299 8.993 3.883L9 4V5H15V4C15 3.73478 15.1054 3.48043 15.2929 3.29289C15.4804 3.10536 15.7348 3 16 3ZM19 12H5V19H19V12ZM19 7H5V10H19V7Z"
                fill="#AEAEAE"
              />
            </g>
            <defs>
              <clipPath id="clip0_28279_21587">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <span>{durationMin ? dateLineClasses : dateLineOpenGyms}</span>
        </div>

        {instructor && (
          <div className="confirmation-modal__row">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g clipPath="url(#clip0_27455_4435)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 13C14.396 13 16.575 13.694 18.178 14.672C18.978 15.16 19.662 15.736 20.156 16.362C20.642 16.977 21 17.713 21 18.5C21 19.345 20.589 20.011 19.997 20.486C19.437 20.936 18.698 21.234 17.913 21.442C16.335 21.859 14.229 22 12 22C9.771 22 7.665 21.86 6.087 21.442C5.302 21.234 4.563 20.936 4.003 20.486C3.41 20.01 3 19.345 3 18.5C3 17.713 3.358 16.977 3.844 16.361C4.338 15.736 5.021 15.161 5.822 14.671C7.425 13.695 9.605 13 12 13ZM12 15C9.977 15 8.157 15.59 6.864 16.379C6.217 16.773 5.729 17.201 5.414 17.601C5.09 18.011 5 18.321 5 18.5C5 18.622 5.037 18.751 5.255 18.926C5.504 19.126 5.937 19.333 6.599 19.508C7.917 19.858 9.811 20 12 20C14.19 20 16.083 19.857 17.4 19.508C18.063 19.333 18.496 19.126 18.745 18.926C18.963 18.751 19 18.622 19 18.5C19 18.32 18.91 18.011 18.587 17.601C18.271 17.201 17.783 16.773 17.136 16.379C15.843 15.589 14.023 15 12 15ZM12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7C17 8.32608 16.4732 9.59785 15.5355 10.5355C14.5979 11.4732 13.3261 12 12 12C10.6739 12 9.40215 11.4732 8.46447 10.5355C7.52678 9.59785 7 8.32608 7 7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2ZM12 4C11.606 4 11.2159 4.0776 10.8519 4.22836C10.488 4.37913 10.1573 4.6001 9.87868 4.87868C9.6001 5.15726 9.37913 5.48797 9.22836 5.85195C9.0776 6.21593 9 6.60603 9 7C9 7.39397 9.0776 7.78407 9.22836 8.14805C9.37913 8.51203 9.6001 8.84274 9.87868 9.12132C10.1573 9.3999 10.488 9.62087 10.8519 9.77164C11.2159 9.9224 11.606 10 12 10C12.7956 10 13.5587 9.68393 14.1213 9.12132C14.6839 8.55871 15 7.79565 15 7C15 6.20435 14.6839 5.44129 14.1213 4.87868C13.5587 4.31607 12.7956 4 12 4Z"
                  fill="#AEAEAE"
                />
              </g>
              <defs>
                <clipPath id="clip0_27455_4435">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <span>
              {t("Instructor")}: {instructor}
            </span>
          </div>
        )}

        {typeof credits === "number" && (
          <div className="confirmation-modal__row">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g clipPath="url(#clip0_27455_4441)">
                <path
                  d="M19 4C19.7956 4 20.5587 4.31607 21.1213 4.87868C21.6839 5.44129 22 6.20435 22 7V17C22 17.7956 21.6839 18.5587 21.1213 19.1213C20.5587 19.6839 19.7956 20 19 20H5C4.20435 20 3.44129 19.6839 2.87868 19.1213C2.31607 18.5587 2 17.7956 2 17V7C2 6.20435 2.31607 5.44129 2.87868 4.87868C3.44129 4.31607 4.20435 4 5 4H19ZM20 10H4V17C4.00003 17.2449 4.08996 17.4813 4.25272 17.6644C4.41547 17.8474 4.63975 17.9643 4.883 17.993L5 18H19C19.2449 18 19.4813 17.91 19.6644 17.7473C19.8474 17.5845 19.9643 17.3603 19.993 17.117L20 17V10ZM17 13C17.2549 13.0003 17.5 13.0979 17.6854 13.2728C17.8707 13.4478 17.9822 13.687 17.9972 13.9414C18.0121 14.1958 17.9293 14.4464 17.7657 14.6418C17.6021 14.8373 17.3701 14.9629 17.117 14.993L17 15H14C13.7451 14.9997 13.5 14.9021 13.3146 14.7272C13.1293 14.5522 13.0178 14.313 13.0028 14.0586C12.9879 13.8042 13.0707 13.5536 13.2343 13.3582C13.3979 13.1627 13.6299 13.0371 13.883 13.007L14 13H17ZM19 6H5C4.73478 6 4.48043 6.10536 4.29289 6.29289C4.10536 6.48043 4 6.73478 4 7V8H20V7C20 6.73478 19.8946 6.48043 19.7071 6.29289C19.5196 6.10536 19.2652 6 19 6Z"
                  fill="#AEAEAE"
                />
              </g>
              <defs>
                <clipPath id="clip0_27455_4441">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <span>
              {credits} {t("Credits").split("r")[0].toUpperCase() + t("Credits").split("c")[1]}
            </span>
          </div>
        )}

        <div className="confirmation-modal__section">
          <h4>{t("CancellationPolicy")}</h4>
          <p>
            {t("Cancel")} <strong>{refundCancellationHours}</strong>{" "}
            {t("CancellationPolicyDescription")}
          </p>
        </div>

        {isLoyaltyDealAvailable && (
          <RedeemLoyaltyCheckbox points={credits!} onChange={setIsUsingLoyalty} />
        )}

        <div className="confirmation-modal__actions">
          <button className="confirmation-modal__btn outlined" onClick={onClose} disabled={loading}>
            {t("Cancel")}
          </button>
          <button className="confirmation-modal__btn filled" onClick={onConfirm} disabled={loading}>
            {loading ? <Spin size="small" /> : t("BookNow")}
          </button>
        </div>

        {loading && (
          <div className="confirmation-modal__overlay">
            <Spin size="large" />
          </div>
        )}
      </div>
    </Modal>
  );
};
